/**
 * Timing constants used across the app.
 *
 * Per coding-standards §17: every duration with domain meaning is named
 * and carries its unit in the identifier.
 */

/** How long the cross-origin probe iframe waits for a postMessage reply. */
export const PROBE_TIMEOUT_MS = 1200;

/** Default safety timeout when waiting for an iframe sync to settle. */
export const IFRAME_SYNC_SAFETY_TIMEOUT_MS = 1500;

/** Pause after iframe `load` before resolving an iframe sync. */
export const IFRAME_SYNC_AFTER_LOAD_MS = 250;

/** Time SyncLogout waits before attempting `window.close` for popup cases. */
export const POPUP_SELF_CLOSE_DELAY_MS = 200;

/** Time after a sync broadcast before clearing the session-sync storage key. */
export const SESSION_SYNC_CLEAR_DELAY_MS = 100;

/** Login card fade-in delay on the Login page. */
export const LOGIN_CARD_FADE_IN_DELAY_MS = 100;

/** How long ProfileDetails waits before closing on mouseleave. */
export const PROFILE_MENU_CLOSE_DELAY_MS = 120;

/**
 * Default axios request timeout.
 *
 * Sized to accommodate the slow permission/metadata endpoints on
 * heavily-loaded shared backends — production logs showed legitimate
 * `/v2/users/composer/permission/{userId}` responses arriving at 11–14 s,
 * which the previous 10 s ceiling rejected. The 401-refresh interceptor
 * adds another round-trip on top, so a 120 s upper bound covers slow
 * long-running endpoints while still bounding a true network hang.
 */
export const HTTP_REQUEST_TIMEOUT_MS = 120_000;

/**
 * How long `DirectAuth` waits for the Vue opener to postMessage the auth
 * handoff payload before giving up and bouncing back to the Vue login.
 *
 * Sized to match `HTTP_REQUEST_TIMEOUT_MS` (30 s): the same heavily-loaded
 * shared backend that makes permission/metadata calls arrive at 11–14 s can
 * delay the Vue boot + handoff postMessage well past the old 10 s ceiling,
 * which then dropped a still-valid session at the login page. 30 s gives the
 * handoff the same headroom the HTTP layer already allows.
 */
export const AUTH_HANDOFF_TIMEOUT_MS = 30_000;

/**
 * Soft TTL for cached permission / mastery data. After this age the cache
 * still serves a value but a background refresh kicks off (SWR semantics).
 */
export const CACHE_SOFT_TTL_MS = 5 * 60 * 1000;

/**
 * Hard TTL. The cache will not serve a value older than this without
 * awaiting a fresh fetch.
 */
export const CACHE_HARD_TTL_MS = 60 * 60 * 1000;

/**
 * Per-dependency safety ceiling inside `useDependencyLoader`. If a single
 * step exceeds this, the loader aborts it and reports an error so the user
 * can retry instead of staring at a permanent spinner.
 */
export const DEPENDENCY_STEP_TIMEOUT_MS = 15_000;

/**
 * Backstop age for a Marketing screen cache entry
 * (`features/amazonAds/api/marketingCache.ts`).
 *
 * Freshness is normally decided by the 08:00 America/Chicago boundary, not by an
 * age — an entry is checked against the server the first time it is read after
 * that boundary. This exists for the case where that never happens: a browser
 * whose clock and whose server-`Date` offset are both unusable, or a record left
 * behind by a build old enough that its version stamp no longer matches anything
 * we would recognise. A week is far beyond any legitimate cached-and-revalidated
 * lifetime, so reaching it means something is wrong and a full refetch is right.
 */
export const MARKETING_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How many response records the Marketing cache may hold before it is cleared
 * wholesale.
 *
 * Not a duration, but it belongs beside the age above: the two together are what
 * bound the store. The four dashboard reads plus the paged grids produce roughly
 * a dozen entries in an ordinary session; the grids can multiply that by their
 * page / sort / search permutations, which is unbounded in principle. Set high
 * enough that a normal session never reaches it — the penalty for crossing it is
 * one cold reload, since ranking entries by age would mean reading every payload
 * to find the oldest.
 */
export const MARKETING_CACHE_MAX_ENTRIES = 120;

/**
 * The most days of history the Marketing cache will hold for one entry.
 *
 * A ceiling on what the BROWSER keeps, not on what the screen can show: a request reaching
 * further back is still served, it is just not stored beyond this depth. Matches
 * `DEFAULT_WINDOW_DAYS`, so the window the screen opens with fits exactly — if the two ever
 * diverge with the window the larger, the screen's own first payload becomes uncacheable and
 * every load pays for it.
 */
export const MARKETING_CACHE_MAX_DAYS = 90;

/**
 * Minimum time (ms) the dependency-loader timeline stays visible before
 * flipping to `complete` and rendering children. Prevents the loader
 * from being invisible on warm boots where every step resolves from
 * cache within a single render tick. 700 ms is long enough to register
 * visually without feeling sluggish.
 */
export const MIN_DEPENDENCY_DISPLAY_MS = 700;


/**
 * Safety timeout (ms) for pages that must wait for `useCurrentUserId()`
 * to resolve before they can build their dependency list. If userId
 * stays null beyond this ceiling the page shows an error/retry state
 * instead of hanging forever. Sized to cover the worst-case Vue handoff
 * (iframe load + postMessage + storage event) with generous headroom.
 */
export const USERID_WAIT_TIMEOUT_MS = 15_000;

/**
 * Quiet period after the last CEO Pricing column-width write before the layout
 * is saved to the backend. A resize drag emits a write per animation frame;
 * without the wait every frame would POST.
 */
export const CEO_PRICING_WIDTH_FLUSH_DEBOUNCE_MS = 800;

/**
 * How long a cached CEO Pricing column-settings answer is trusted. Long enough
 * that the page load the cache exists for never pays for a request, short
 * enough that a layout saved on another device lands within a day rather than
 * never.
 */
export const CEO_COLUMN_SETTINGS_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * Poll interval for the CEO Pricing DETAIL background job poller. Deliberately
 * slower than the foreground poller: the background poller only needs to notice
 * that a job finished, so a 30 s tick avoids hammering Quartz for a check the
 * user is not actively waiting on.
 */
export const CEO_PRICING_BG_POLL_INTERVAL_MS = 30_000;

/**
 * Safety ceiling for the CEO Pricing DETAIL background job poller. CEO pricing
 * jobs can legitimately take several minutes and the grid stays locked until the
 * job completes, so the poller must not give up before then — five minutes
 * covers a real job while still bounding a genuinely stalled one.
 */
export const CEO_PRICING_BG_POLL_TIMEOUT_MS = 300_000;

/**
 * Wall-clock unit conversions for relative ages and durations
 * ("5 hours ago", "2d 7h 46m"). One definition — several features used
 * to re-declare `DAY_MS` locally.
 */
export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

/** Staged-orders resend modal — auto-close delay after a successful
 *  send (EmailInvoiceModal parity; moved from a component-local
 *  constant so timing lives with its peers). */
export const STAGED_RESEND_SUCCESS_CLOSE_MS = 1500;

/** Orders Share-Invoice email modal — auto-close delay after a successful
 *  send (mirrors Vue's `HkMailPopUp`; same figure as the staged resend). */
export const EMAIL_SENT_AUTO_CLOSE_MS = 1500;

/** Staged-order approve / cancel — waits before each server re-check after
 *  a save. The backend indexes the write asynchronously: a refetch 1.5s
 *  after a PO update came back with the OLD PO (a second manual refresh
 *  showed the new one). The first wait is Vue's own 3s page reload
 *  (`HkDealerOrderApprovalBlock`); the later ones cover a slower index.
 *  Between checks the cache keeps the saved value (`reconcileStagedOrder`). */
export const STAGED_ORDER_RECONCILE_DELAYS_MS = [3000, 3000, 4000] as const;

/** Listing free-text search — debounce before the value reaches a query
 *  key (`performance.md`: named constant, ~300 ms). */
export const SEARCH_INPUT_DEBOUNCE_MS = 300;
